// src/features/dashboard/components/AppList.tsx

import {
  useAppList,
} from '../hooks/useAppList';

type AppListProps = {
  ManagerEmployeeCode: string;
};

/**
 * App List Component
 *
 * Responsive behavior:
 *
 * 100%  -> 2 columns
 * 110%  -> 2 columns
 * 125%  -> 2 columns
 * 150%  -> 2 columns
 *
 * The layout does not depend on the xl breakpoint.
 */
const AppList = ({
  ManagerEmployeeCode,
}: AppListProps) => {
  const {
    productiveApps,
    nonProductiveApps,
    loading,
    error,
  } = useAppList(
    ManagerEmployeeCode,
  );

  /* ============================================================
     LOADING
  ============================================================ */

  if (loading) {
    return (
      <div
        className="
          flex
          min-h-[280px]
          w-full
          min-w-0
          items-center
          justify-center

          rounded-[16px]
          border
          border-[#E5E7EB]

          bg-white

          px-4
          py-6

          text-[13px]
          text-[#667085]
        "
      >
        Loading applications...
      </div>
    );
  }

  /* ============================================================
     ERROR
  ============================================================ */

  if (error) {
    return (
      <div
        className="
          flex
          min-h-[280px]
          w-full
          min-w-0

          flex-col
          items-center
          justify-center

          rounded-[16px]
          border
          border-[#FECACA]

          bg-[#FEF2F2]

          px-4
          py-6

          text-center
        "
      >
        <h3
          className="
            text-[13px]
            font-semibold
            text-[#DC2626]
          "
        >
          Application List Error
        </h3>

        <p
          className="
            mt-1
            max-w-full
            break-words

            text-[11px]
            text-[#7F1D1D]
          "
        >
          {error}
        </p>
      </div>
    );
  }

  /* ============================================================
     MAIN
  ============================================================ */

  return (
    <div
      className="
        grid
        w-full
        min-w-0
        max-w-full

        grid-cols-2

        gap-3
        sm:gap-4

        items-stretch
      "
    >
      {/* ========================================================
          PRODUCTIVE APPLICATIONS
      ======================================================== */}

      <div
        className="
          w-full
          min-w-0
          max-w-full
          overflow-hidden

          rounded-[16px]
          border
          border-[#D9DDE3]

          bg-white

          px-3
          py-3

          sm:px-4
          sm:py-4
        "
      >
        {/* Header */}

        <div
          className="
            mb-4

            flex
            min-w-0
            flex-wrap
            items-center
            gap-2
          "
        >
          <h2
            className="
              min-w-0
              max-w-full

              text-[13px]
              sm:text-[15px]

              font-bold
              text-[#002B5B]

              leading-5
            "
          >
            Top 5 Productive Applications
          </h2>

          <div
            className="
              max-w-full

              rounded-full
              bg-[#EAF2FF]

              px-2
              py-1

              text-[9px]
              sm:text-[10px]

              font-medium
              text-[#2563EB]

              whitespace-nowrap
            "
          >
            Avg hours/day per associate
          </div>
        </div>

        {/* Productive Apps */}

        {productiveApps.map(
          (app, index) => (
            <div
              key={index}
              className={`
                min-w-0
                py-3

                ${
                  index !==
                  productiveApps.length - 1
                    ? 'border-b border-[#E9EDF3]'
                    : ''
                }
              `}
            >
              <div
                className="
                  flex
                  min-w-0
                  items-center
                  justify-between

                  gap-2
                  sm:gap-3
                "
              >
                {/* App Information */}

                <div
                  className="
                    min-w-0
                    w-[110px]
                    sm:w-[150px]
                    lg:w-[160px]
                    shrink-0
                  "
                >
                  <h3
                    className="
                      truncate

                      text-[11px]
                      sm:text-[13px]

                      font-semibold
                      text-[#374151]
                    "
                    title={app.name}
                  >
                    {app.name}
                  </h3>

                  <p
                    className="
                      mt-[2px]

                      truncate

                      text-[10px]
                      sm:text-[12px]

                      text-[#64748B]
                    "
                    title={app.subtitle}
                  >
                    {app.subtitle}
                  </p>
                </div>

                {/* Progress */}

                <div
                  className="
                    flex
                    min-w-0
                    flex-1
                    items-center

                    gap-2
                    sm:gap-3
                  "
                >
                  <div
                    className="
                      h-[5px]
                      min-w-0
                      flex-1

                      overflow-hidden
                      rounded-full

                      bg-[#DDF5EA]
                    "
                  >
                    <div
                      className="
                        h-full
                        max-w-full

                        rounded-full

                        bg-[#10B981]

                        transition-all
                        duration-300
                      "
                      style={{
                        width: `${Math.min(
                          Math.max(
                            app.percentage,
                            0,
                          ),
                          100,
                        )}%`,
                      }}
                    />
                  </div>

                  <span
                    className="
                      w-[32px]
                      sm:w-[40px]
                      shrink-0

                      text-right

                      text-[10px]
                      sm:text-[12px]

                      font-semibold
                      text-[#002B5B]
                    "
                  >
                    {app.percentage}%
                  </span>
                </div>
              </div>
            </div>
          ),
        )}
      </div>

      {/* ========================================================
          NON-PRODUCTIVE APPLICATIONS
      ======================================================== */}

      <div
        className="
          w-full
          min-w-0
          max-w-full
          overflow-hidden

          rounded-[16px]
          border
          border-[#D9DDE3]

          bg-white

          px-3
          py-3

          sm:px-4
          sm:py-4
        "
      >
        {/* Header */}

        <div
          className="
            mb-4

            flex
            min-w-0
            flex-wrap
            items-center
            gap-2
          "
        >
          <h2
            className="
              min-w-0
              max-w-full

              text-[13px]
              sm:text-[15px]

              font-bold
              text-[#002B5B]

              leading-5
            "
          >
            Top 5 Non-Productive Applications
          </h2>

          <div
            className="
              max-w-full

              rounded-full
              bg-[#EAF2FF]

              px-2
              py-1

              text-[9px]
              sm:text-[10px]

              font-medium
              text-[#2563EB]

              whitespace-nowrap
            "
          >
            Avg hours/day per associate
          </div>
        </div>

        {/* Non-Productive Apps */}

        {nonProductiveApps.map(
          (app, index) => (
            <div
              key={index}
              className={`
                min-w-0
                py-3

                ${
                  index !==
                  nonProductiveApps.length - 1
                    ? 'border-b border-[#E9EDF3]'
                    : ''
                }
              `}
            >
              <div
                className="
                  flex
                  min-w-0
                  items-center
                  justify-between

                  gap-2
                  sm:gap-3
                "
              >
                {/* App Information */}

                <div
                  className="
                    min-w-0
                    w-[110px]
                    sm:w-[150px]
                    lg:w-[160px]
                    shrink-0
                  "
                >
                  <h3
                    className="
                      truncate

                      text-[11px]
                      sm:text-[13px]

                      font-semibold
                      text-[#374151]
                    "
                    title={app.name}
                  >
                    {app.name}
                  </h3>

                  <p
                    className="
                      mt-[2px]

                      truncate

                      text-[10px]
                      sm:text-[12px]

                      text-[#64748B]
                    "
                    title={app.subtitle}
                  >
                    {app.subtitle}
                  </p>
                </div>

                {/* Progress */}

                <div
                  className="
                    flex
                    min-w-0
                    flex-1
                    items-center

                    gap-2
                    sm:gap-3
                  "
                >
                  <div
                    className="
                      h-[5px]
                      min-w-0
                      flex-1

                      overflow-hidden
                      rounded-full

                      bg-[#FFE5D6]
                    "
                  >
                    <div
                      className="
                        h-full
                        max-w-full

                        rounded-full

                        bg-[#FF0000]

                        transition-all
                        duration-300
                      "
                      style={{
                        width: `${Math.min(
                          Math.max(
                            app.percentage,
                            0,
                          ),
                          100,
                        )}%`,
                      }}
                    />
                  </div>

                  <span
                    className="
                      w-[32px]
                      sm:w-[40px]
                      shrink-0

                      text-right

                      text-[10px]
                      sm:text-[12px]

                      font-semibold
                      text-[#002B5B]
                    "
                  >
                    {app.percentage}%
                  </span>
                </div>
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default AppList;